import {useEffect, useState} from "react";
import {useUser} from "UserProvider";
import {SubscriptionElementsCheckout} from "pay/SubscriptionElementsCheckout";
import {useLocale, useMessages} from "i18n";
import {formatMoney} from "pricing/PricingStorage";
import {ImmediateDeliveryConsent} from "pay/ImmediateDeliveryConsent";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {withdrawalEffectOf} from "pay/withdrawalRight";
import {
	EXIT_OFFER_INTRO_DAYS, exitOfferPriceFor, exitOfferRegularPriceFor, type ExitOfferAmounts,
} from "upsell/exitOffer";
import {useExitOfferPurchase} from "upsell/useExitOfferPurchase";
import {
	AfterNote, Continue, Eyebrow, Panel, Title, Unavailable,
} from "./LadderIntroOffer.styles";

/**
 * The day-14 ladder rung on `/pricing` (kitecorp/transferify-ui#1977).
 *
 * <b>It is the exit offer, reached a different way.</b> The rung's terms are the exit offer's intro
 * Price plus the trial that already ships, and the ladder differs only in how eligibility is
 * established: by the emailed token instead of by closing the pricing dialog. So every figure, every
 * sentence and the whole purchase path are the exit offer's own — {@link exitOfferPriceFor} for the
 * amounts, the `exitOffer` catalogue for the words, {@link useExitOfferPurchase} for the checkout,
 * its sign-in and its refusal handling. Nothing about money is restated here.
 *
 * <b>ONE term, not two.</b> The dialog offers the fortnight beside a free trial and lets the reader
 * pick; this panel offers only what the message offered. The api answers a day-14 link with the
 * `intro14` variant and nothing else, so a second term on screen would be an offer the link never
 * carried.
 *
 * <b>The token rides with the purchase</b>, so the api applies the rung and attributes the sale. It
 * is also what silences this checkout's client-side funnel step: the ladder's surfaces are
 * server-witnessed, and {@link useExitOfferPurchase} asks the rule rather than reporting.
 *
 * <b>A token the api refuses at the till is reported UP, not shown here.</b> The link is dead, so this
 * panel has nothing left to offer and the page owes the reader the same expired notice a dead link
 * gets. Handling it in place would leave an offer on screen that cannot be bought.
 *
 * @param onOfferExpired the api refused the token; nothing was charged and this panel must give way
 */
export function LadderIntroOffer(
	{amounts, token, onOfferExpired}: {
		amounts: ExitOfferAmounts;
		token: string;
		onOfferExpired: () => void;
	},
) {
	const {exitOffer, pricing, checkout} = useMessages();
	const [user] = useUser();
	const [placed, setPlaced] = useState(false);
	const {locale} = useLocale();
	const contract = useCheckoutDocument(true);
	const {
		continueWithOffer, offerUnavailable, offerExpired, continueAtRegularPrice, redirecting,
		acknowledged, setAcknowledged, admission, clientSecret, collectsShippingAddress, error, dismiss, completed,
	} = useExitOfferPurchase(token, contract.latestRead);

	// Raised in an effect rather than from the click handler so it fires for BOTH presses — the offer
	// one and the deliberate regular-price one — from the single fact the hook holds, instead of two
	// call sites that could come to disagree about which outcomes count.
	useEffect(() => {
		if (offerExpired) {
			onOfferExpired();
		}
	}, [offerExpired, onOfferExpired]);

	const introPrice = formatMoney(amounts.currency, amounts.intro, locale);
	const monthlyPrice = formatMoney(amounts.currency, amounts.monthly, locale);

	if (placed) return <Unavailable role="status">{pricing.upgradeDialog.confirming}</Unavailable>;
	if (clientSecret !== null) return <SubscriptionElementsCheckout
		plan="premium" clientSecret={clientSecret} collectsShippingAddress={collectsShippingAddress}
		contractDocument={contract.document}
		awaitingDocument={contract.awaitingDocument} consented={acknowledged} buyerEmail={user.email === "" ? undefined : user.email}
		onConsentChange={checked => { setAcknowledged(checked); if (!checked) dismiss(); }}
		monthlyPrice={monthlyPrice}
		introTerms={offerUnavailable ? undefined : exitOffer.intro.title(introPrice, EXIT_OFFER_INTRO_DAYS)}
		onPlaced={() => { completed(); setPlaced(true); }}
	/>;

	return (
		<Panel aria-label={exitOffer.dialogAria}>
			<Eyebrow>{exitOffer.eyebrow}</Eyebrow>
			{offerUnavailable ? (
				// The term goes with the offer: leaving the fortnight on screen would keep offering
				// something the api has just refused.
				<Unavailable role="status" data-testid="ladder-intro-unavailable">
					{exitOffer.unavailable.lead}<strong>{monthlyPrice}</strong>{exitOffer.unavailable.trail}
				</Unavailable>
			) : (
				<>
					<Title>{exitOffer.intro.title(introPrice, EXIT_OFFER_INTRO_DAYS)}</Title>
				</>
			)}
			{/*
			  * The renewal price, stated above the CTA rather than as fine print — the acceptance
			  * criterion is that the intro amount AND the renewal are both visible before the reader
			  * commits, and fine print under a button is not visible in that sense.
			  */}
			<AfterNote>
				{exitOffer.then.lead}<strong>{monthlyPrice}</strong>{exitOffer.then.trail}
			</AfterNote>
			{/*
			  * The express consent, immediately above the control it gates (kitecorp/transferify-ui#1844).
			  * A SUBSCRIPTION, so the reader is shown the proportionate-payment statement; which sentence
			  * that is comes from the rule module, never from a choice made here.
			  */}
			<ImmediateDeliveryConsent
				contractDocument={contract.document}
				// The CTA below already waits; the box does too, so the reader cannot agree to terms the
				// api has not yet named (ui#2562). It holds the box only while it is UNTICKED, so a reader
				// who has agreed keeps their own ability to change their mind.
				awaitingDocument={contract.awaitingDocument}
				effect={withdrawalEffectOf("subscription")}
				checked={acknowledged}
				onChange={setAcknowledged}
				testId="ladder-intro-consent"
			/>
			{/*
			  * One CTA, two meanings, and the label is what tells them apart. The second press is
			  * deliberate: nothing is charged by the refusal itself, so the reader reads the new price and
			  * decides again rather than being carried to it.
			  */}
			<Continue
				type="button"
				disabled={redirecting || admission === "acknowledgementRequired" || contract.awaitingDocument}
				// Busy from the press on, including while it waits for a restored sign-in (ui#3117).
				aria-busy={redirecting}
				data-testid="ladder-intro-cta"
				onClick={event => void (offerUnavailable
					? continueAtRegularPrice("intro14", exitOfferRegularPriceFor(amounts, "intro14"))
					: continueWithOffer("intro14", exitOfferPriceFor(amounts, "intro14"), event.currentTarget))}
			>
				{offerUnavailable ? exitOffer.unavailable.cta : exitOffer.cta}
			</Continue>
			{error !== null && <Unavailable role="alert">{checkout.errors[error]}</Unavailable>}
		</Panel>
	);
}
